'use client';

/* eslint-disable no-console */
/* eslint-disable no-alert */

import omit from 'lodash/omit';
import React, {
  useState, useRef, FC, ChangeEvent, FormEvent,
} from 'react';

import { formsBaseUrl, legalConsentOptions } from '../../../site-config';
import PartCheckbox from '@/components/UI/part-checkbox';
import PartInput from '@/components/UI/part-input';
import PartSelect from '@/components/UI/part-select';
import { IDetailes } from '@/contentful/utils/detailes';
import { IForm } from '@/contentful/utils/form';
import { IFormData } from '@/types/index.types';

import IconClock from './assets/icon-clock.svg';
import IconEmail from './assets/icon-email.svg';
import IconLineLeft from './assets/icon-line-left-small.svg';
import IconPhone from './assets/icon-phone.svg';
import IconPin from './assets/icon-pin.svg';

import styles from './part-contact-deatiles.module.scss';

interface PartContactDetailesProps {
  data: {
    category: string | undefined;
    title: string | undefined;
    detailes: IDetailes;
    form: IForm;
  };
}

const PartFeedback: FC<PartContactDetailesProps> = ({ data }) => {
  const {
    workingHoursFull, phones, emails, addressFull,
  } = data.detailes;

  const {
    nameFields,
    emailField,
    phoneField,
    servicesListDefault,
    servicesList,
    messageField,
    button,
    formSuccessText,
    formSuccessTitle,
    errorMessageMin,
    errorMessageName,
    errorMessageEmail,
    errorMessagePhone,
    labelCheckBox,
  } = data.form;

  const [formData, setFormData] = useState<IFormData>({});
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [formSuccess, setFormSuccess] = useState<boolean>(false);
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const formRef = useRef<HTMLFormElement>(null);

  const handleChangeInput = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      [e.target.name]: e.target.value,
    }));
    setFormErrors((prevFormErrors) => omit(prevFormErrors, [e.target.name]));
  };

  const handleCheckboxChange = (checked: boolean) => {
    setIsChecked(checked);
  };

  const handleChangeSelect = (option: string) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      service: option,
    }));
  };

  const validateSubmit = () => {
    const errors: { firstname?: string; email?: string; phone?: string } = {};
    const { firstname, email, phone } = formData;

    if (firstname && firstname.length < 2) {
      errors.firstname = errorMessageMin ?? 'Min 2 characters';
    }
    if (!firstname) errors.firstname = errorMessageName ?? 'Please enter a name';
    if (!email?.match(/^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i)) {
      errors.email = errorMessageEmail ?? 'Please enter a valid email';
    }
    if (!phone?.match(/^[+]?[\s./0-9]*[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/g)) {
      errors.phone = errorMessagePhone ?? 'Please enter a valid phone';
    }

    setFormErrors(errors);

    const isValid = Object.keys(errors).length === 0;

    return isValid;
  };

  // eslint-disable-next-line consistent-return
  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateSubmit()) return false;

    const fields = [];

    fields.push(
      ...Object.entries(formData).map(([k, v]) => ({
        name: k,
        value: v,
      })),
    );

    const currentUrl = window.location.pathname;

    fields.push({
      name: 'conversion_url',
      value: currentUrl,
    });

    fetch(`${formsBaseUrl}/991c3cca-f95e-4483-a838-ec324d5894db`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fields, legalConsentOptions }),
    })
      .then(() => {
        setFormData({});
        setFormErrors({});
        setFormSuccess(true);
      })
      .catch((error) => {
        alert('Something went wrong. Please try again later.');
        console.log(error);
      });
  };

  const companyPhoneLink = phones?.match(/\+?\d+/g)?.join('');

  return (
    <section className={styles.container}>
      <div className={styles.content}>
        <div className={styles.categoryContainer}>
          <IconLineLeft />
          <p className={styles.category}>{data.category}</p>
        </div>
        <h2 className={styles.sectionTitle}>{data.title}</h2>

        <ul className={styles.contactsWrap}>
          <li className={styles.contactItem}>
            <div className={styles.contactItemIconWrap}>
              <IconClock />
            </div>
            <div className={styles.contactItemContentWrap}>
              <p className={styles.contactItemText}>{workingHoursFull[0]}</p>
              <p className={styles.contactItemText}>{workingHoursFull[1]}</p>
            </div>
          </li>

          <li className={styles.contactItem}>
            <div className={styles.contactItemIconWrap}>
              <IconPhone />
            </div>
            <div className={styles.contactItemContentWrap}>
              <a
                href={`tel:${companyPhoneLink}`}
                rel="nofollow noreferrer"
                target="_blank"
                className={styles.contactLink}
              >
                <span className={styles.contactLinkText}>{phones}</span>
              </a>
            </div>
          </li>
          <li className={styles.contactItem}>
            <div className={styles.contactItemIconWrap}>
              <IconEmail />
            </div>
            <div className={styles.contactItemContentWrap}>
              <a
                href={`mailto:${emails}`}
                rel="nofollow noreferrer"
                target="_blank"
                className={styles.contactLink}
              >
                <span className={styles.contactLinkText}>{emails}</span>
              </a>
            </div>
          </li>

          <li className={styles.contactItem}>
            <div className={styles.contactItemIconWrap}>
              <IconPin />
            </div>
            <div className={styles.contactItemContentWrap}>
              <p className={styles.contactItemText}>{addressFull[0]}</p>
              <p className={styles.contactItemText}>{addressFull[1]}</p>
            </div>
          </li>
        </ul>
      </div>
      <div className={styles.formWrap}>
        <h2 className={styles.title}>{data.form.title}</h2>
        <form className={styles.form} ref={formRef} onSubmit={handleSubmit}>
          <div className={styles.formInputsContainer}>
            <PartInput
              name="firstname"
              placeholder={nameFields}
              onChange={handleChangeInput}
              value={formData.firstname}
              inputError={formErrors.firstname}
            />
            <PartInput
              name="email"
              placeholder={emailField}
              onChange={handleChangeInput}
              value={formData.email}
              inputError={formErrors.email}
            />
            <PartInput
              name="phone"
              placeholder={phoneField ?? ''}
              onChange={handleChangeInput}
              value={formData.phone}
              inputError={formErrors.phone}
            />
            <PartSelect
              options={servicesList}
              getSelectData={handleChangeSelect}
              defaultListValue={servicesListDefault}
            />
            <PartCheckbox
              checked={isChecked}
              onChange={handleCheckboxChange}
              label={labelCheckBox ?? ''}
            />
          </div>
          <PartInput
            type="textarea"
            name="message"
            placeholder={messageField || ''}
            value={formData.message || ''}
            onChange={handleChangeInput}
          />
          <div className={styles.submitButtonContainer}>
            {/* eslint-disable-next-line jsx-a11y/mouse-events-have-key-events */}
            <button
              className={styles.submitButton}
              type="submit"
              onClick={validateSubmit}
              disabled={Object.keys(formErrors).length > 0 || !isChecked}
              title={
                Object.keys(formErrors).length > 0
                  ? 'Form has errors'
                  : undefined
              }
            >
              <span>{button}</span>
            </button>
          </div>
        </form>
        {formSuccess && (
          <div className={styles.formSuccess}>
            <h3 className={styles.formSuccessTitle}>
              {formSuccessTitle ?? 'Thank you!'}
            </h3>
            <p className={styles.formSuccessText}>
              {formSuccessText ?? 'We will contact you soon.'}
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default PartFeedback;
